/**
 * ContactListerButton Component (Client Island)
 *
 * Diamond Architecture: Client Component for auth-gated interactivity.
 *
 * 4 States (checked in priority order):
 * 1. Lister (owns listing) → "Manage Listing" → LINK to the account page
 * 2. Inactive → "Not Available" → Disabled button
 * 3. Existing Conversation → "Continue Conversation" → LINK to the chat
 * 4. Default → "Message Lister" → LINK to the inquiry page, auth-gated
 *
 * Three of the four states are NAVIGATIONS, and they are rendered as real
 * anchors (LET-83). They used to be `Button onClick={() => router.push(…)}`:
 * on every listing detail page — an indexable public page — the control that
 * leads to the inquiry form carried no `href`, so a crawler had no path to
 * `/listings/{slug}/inquiry`, a reader with no JS got a dead button, and a
 * human could not middle-click or copy the link. The state discriminant is now
 * `kind: 'link' | 'disabled'`: a link state MUST carry its `href`, so a fourth
 * navigation state cannot be added without saying where it goes.
 *
 * State 4 is auth-gated through the seam's `gate`: with JS a signed-out click
 * opens the modal in place, and the anchor still points at the real inquiry
 * URL the server answers for.
 *
 * Each state publishes a copy-free token on `data-contact-state`, and each
 * variant a `data-testid`. Both exist for the LET-81 e2e regression spec
 * (`web/e2e/public/authenticated/inquiry-gate.spec.ts`), which has to tell
 * state 3 from state 4 — the exact flip the bug caused — and cannot do it on
 * the control's TEXT, because that text comes from the translations stack and
 * changes per locale. The token is the discriminant `getContactControl`
 * already computes; publishing it costs one attribute and removes a whole
 * class of copy-coupled test.
 */
import { EnhancedLink } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { useAuthGate } from '@/shared/providers/AuthProvider';
import type { IntentTypeValue } from '@letsgethome/lib/types/listings';
import { routes, type Route } from '@letsgethome/routes';
import {
  slug as toSlug,
  type ConversationId,
  type ListingId,
  type LocalizedText,
} from '@letsgethome/types/branded';
import { Button } from '@letsgethome/ui/button';
import { MessageSquare, Settings } from 'lucide-react';

/**
 * The control's state, as a locale-independent token. Mirrors the four
 * branches of `getContactControl` below, in the same priority order, and is
 * published on `data-contact-state`.
 */
type ContactState = 'manage-listing' | 'not-available' | 'continue-conversation' | 'message-lister';

/**
 * What the control IS. A `link` state carries the URL it leads to — required,
 * so "navigates, but to nowhere the HTML can express" is unrepresentable — and
 * optionally the seam `gate` that may consume a plain click (the auth modal).
 * `rel` is the per-link crawl statement for a private `/account` destination.
 */
type ContactControl = {
  state: ContactState;
  // `LocalizedText`, NOT `string`: `t()` returns the brand, and widening it
  // here would make `{config.text}` a bare string at the JSX boundary —
  // which `@letsgethome/no-unlocalized-jsx-text` correctly refuses.
  text: LocalizedText;
  icon: typeof MessageSquare;
} & ({ kind: 'link'; href: Route; gate?: () => boolean; rel?: 'nofollow' } | { kind: 'disabled' });

type Props = {
  listingId: ListingId;
  listingSlug: string;
  isLister: boolean;
  isActive: boolean;
  existingConversationId?: ConversationId;
  defaultIntentValue?: IntentTypeValue;
  variant?: 'desktop' | 'mobile';
};

export function ContactListerButton({
  listingId,
  listingSlug,
  isLister,
  isActive,
  existingConversationId,
  defaultIntentValue,
  variant = 'desktop',
}: Props) {
  const t = useTranslations('listings.detail');
  const { requireAuth } = useAuthGate();

  // Determine the control's state (priority order)
  const getContactControl = (): ContactControl => {
    // State 1: User is the lister
    if (isLister) {
      return {
        kind: 'link',
        state: 'manage-listing',
        text: t('manageListing'),
        icon: Settings,
        href: routes.account.listings.detail(listingId),
        rel: 'nofollow',
      };
    }

    // State 2: Inactive listing
    if (!isActive) {
      return {
        kind: 'disabled',
        state: 'not-available',
        text: t('notAvailable'),
        icon: MessageSquare,
      };
    }

    // State 3: Existing conversation
    if (existingConversationId) {
      return {
        kind: 'link',
        state: 'continue-conversation',
        text: t('continueConversation'),
        icon: MessageSquare,
        href: routes.account.conversations.detail(existingConversationId),
        rel: 'nofollow',
      };
    }

    // State 4: Default - message lister. The inquiry page is a public route a
    // crawler may follow, so no `rel`; the click is auth-gated in the SPA.
    // Fall back to 'rent' when no default preference was recorded.
    const intentSlug = defaultIntentValue ?? 'rent';
    return {
      kind: 'link',
      state: 'message-lister',
      text: t('messageLister'),
      icon: MessageSquare,
      href: routes.listings.inquiry(toSlug(listingSlug), intentSlug),
      gate: requireAuth,
    };
  };

  const config = getContactControl();
  const Icon = config.icon;
  const testId = variant === 'mobile' ? 'contact-lister-mobile' : 'contact-lister-desktop';

  const control =
    config.kind === 'disabled' ? (
      <Button
        disabled
        className="w-full"
        size="lg"
        data-testid={testId}
        data-contact-state={config.state}
      >
        <Icon className="mr-2 h-5 w-5" />
        {config.text}
      </Button>
    ) : (
      <Button asChild className="w-full" size="lg">
        <EnhancedLink
          href={config.href}
          gate={config.gate}
          rel={config.rel}
          data-testid={testId}
          data-contact-state={config.state}
        >
          <Icon className="mr-2 h-5 w-5" />
          {config.text}
        </EnhancedLink>
      </Button>
    );

  if (variant === 'mobile') {
    return control;
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
      {!isLister && (
        <>
          <h3 className="mb-4 text-lg font-semibold">{t('interestedTitle')}</h3>
          <p className="mb-6 text-sm text-muted-foreground">{t('interestedSubtitle')}</p>
        </>
      )}
      {isLister && <p className="mb-6 text-sm text-muted-foreground">{t('listerSubtitle')}</p>}
      {control}
    </div>
  );
}
